import { Resolver } from '@apollo/client';
import axios from 'axios';
import HttpStatus from 'http-status-codes';

import { getTokenPromise } from '@trqk/utils';

interface Args {
    assetUrl: string;
    doTaskUpdates?: DoTaskUpdates;
    filename: string;
    taskId: string;
    trqkDirectoryUrl: string;
    trqkSongId: string;
    userId: string;
}

interface Context {
    getToken: GetToken;
    sendTaskEvent: SendTaskEvent;
}

interface Result {
    id: Maybe<string>;
    asset_url: Maybe<string>;
    detection_history: DetectionStatusChange[] | null;
    detection_status: Maybe<string>;
    filename: Maybe<string>;
    song_id: Maybe<string>;
}

/**
 * Creates a new TrqkAudio for a user.
 * Accepts an `args.doTaskUpdates` config that determines in which cases the updates will be sent to
 * the TaskProvider state machine using context.sendTaskEvent(). This allows
 * taskCreateTrqkAudio to be wrapped by another task resolver to create a multi-step task.
 *
 * NOTE: since the flow for creating an unmatched track requires two steps, this task should (most
 * likely) not be called on its own. This is why there is no query string exported from this file.
 */
export const taskCreateTrqkAudio: Resolver = async (
    obj: Record<string, unknown>,
    args: Args,
    context: Context,
): Promise<Result> => {
    const {
        assetUrl,
        doTaskUpdates = {
            error: true,
            success: true,
            progress: true,
        },
        filename,
        taskId,
        trqkDirectoryUrl,
        trqkSongId,
        userId,
    } = args;
    const { getToken, sendTaskEvent } = context;
    const token = await getTokenPromise(getToken);

    try {
        const response = await axios({
            method: 'post',
            url: trqkDirectoryUrl,
            headers: {
                Authorization: token,
            },
            // throw an error for non-200 status
            validateStatus: (status: number): boolean =>
                status === HttpStatus.OK,
            data: {
                query: `
                    mutation createTrqkAudio(
                        $input: CreateTrqkAudioInput!
                    ) {
                        createTrqkAudio(input: $input) {
                            id
                            asset_url
                            detection_history {
                                timestamp
                                status
                                source
                            }
                            detection_status
                            filename
                            song_id
                        }
                    }
                `,
                variables: {
                    input: {
                        user_id: userId,
                        filename,
                        asset_url: assetUrl,
                        song_id: trqkSongId,
                    },
                },
            },
        });

        if (response?.data?.errors) {
            throw new Error(
                `Error(s) in GraphQL mutation: \n${JSON.stringify(
                    response.data.errors,
                    null,
                    4,
                )}`,
            );
        }

        const mutationResult = response?.data?.data?.createTrqkAudio;
        const id = mutationResult?.id ?? null;
        const asset_url = mutationResult?.asset_url ?? null;
        const detection_history = mutationResult?.detection_history ?? null;
        const detection_status = mutationResult?.detection_status ?? null;
        const response_filename = mutationResult?.filename ?? null;
        const song_id = mutationResult?.song_id ?? null;

        const newTrqkAudio = {
            id,
            asset_url,
            detection_history,
            detection_status,
            filename: response_filename,
            song_id,
        };

        if (doTaskUpdates.success) {
            sendTaskEvent({
                type: 'SUCCESS',
                value: {
                    taskId,
                    output: {
                        assetUrl,
                        filename,
                        trqkAudio: newTrqkAudio,
                        trqkSongId,
                        userId,
                    },
                },
            });
        }

        return newTrqkAudio;
    } catch (error) {
        if (doTaskUpdates.error) {
            sendTaskEvent({
                type: 'ERROR',
                value: {
                    taskId,
                    error: 'Failed to create new audio for user',
                },
            });
        }

        throw error;
    }
};
